import { useEffect, useRef, useCallback } from 'react';

const VERSION_CHECK_INTERVAL = 300_000; // 5 minutes

interface VersionInfo {
    buildHash: string;
    buildTime: string;
    version: string;
}

export function useVersionCheck(): void {
    const initialHashRef = useRef<string | null>(null);
    const isReloadingRef = useRef(false);

    const checkVersion = useCallback(async () => {
        if (isReloadingRef.current) return;

        try {
            const response = await fetch(`/version.json?_t=${Date.now()}`, {
                cache: 'no-store',
            });

            if (!response.ok) return;

            const data: VersionInfo = await response.json();

            if (initialHashRef.current === null) {
                // First check - store current version, no reload
                initialHashRef.current = data.buildHash;
                return;
            }

            if (data.buildHash !== initialHashRef.current) {
                console.log(
                    `[VersionCheck] New version detected: ${initialHashRef.current} -> ${data.buildHash}. Reloading...`
                );
                isReloadingRef.current = true;

                // Clear all SW caches before reloading so the new index.html is fetched fresh
                if ('caches' in window) {
                    const cacheNames = await caches.keys();
                    await Promise.all(cacheNames.map((name) => caches.delete(name)));
                }

                window.location.reload();
            }
        } catch {
            // Network error - silently ignore, will retry next interval
        }
    }, []);

    useEffect(() => {
        checkVersion();

        const intervalId = setInterval(checkVersion, VERSION_CHECK_INTERVAL);

        return () => clearInterval(intervalId);
    }, [checkVersion]);
}
